import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthService } from '@core/services/core/auth/auth.service';
import { FamilyInfoConfig } from '@models/family';
import { FamiliesOperation } from '@models/operation-register';
import { FamiliesService } from '@modules/families/services/families.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { MenuService } from '../core/menu.service';
import { NavigationHelper } from '../core/navigation-helper.service';
import { OperationRegisterService } from '../core/operation-register.service';
import { UiHelperService } from '../core/ui-helper.service';

@Injectable({
  providedIn: 'root'
})
export class FamiliesHelperService {
  constructor(
    public menu: MenuService,
    private nav: NavigationHelper,
    private ui: UiHelperService,
    private or: OperationRegisterService,
    private familiesService: FamiliesService,
    private trans: TranslateService,
    private sanitizer: DomSanitizer,
    private auth: AuthService
  ) {}

  async navToFamilies(direction: string, pageId: string, fromReturn: boolean, fromDraftNotice = false) {
    await this.ui.presentLoading(pageId);
    this.familiesService.getFamily().pipe(
      finalize(() => this.ui.dismissLoading(pageId))
    ).subscribe(family => {
      this.familiesService.setHasGoneThroughTheFamiliesHelper(true);
      this.familiesService.setFamily(family);
      const inconsistentData = family && (family.duplicated || family.inProcess || !family.canBeInvited || !family.isClient);
      const invitationPending = family && family.invitationPending;
      const userActiveFamily = family && !!family.id;
      const userHasCreatorRole = family && family.canBeCreator;
      const userNoCreatorWithoutFamily = family && !family.duplicated && !family.inProcess &&
          (!userActiveFamily || !family.familyNotUnsubscribed) && !invitationPending && !userHasCreatorRole && family.canBeInvited;
      const minorInMoreFamilies = family && family.minorInMoreFamilies;
      // Active family is not validated because it will always return its id to be able to accept or reject
      const userToStep1 = family && !family.duplicated && !family.inProcess && invitationPending && family.familyNotUnsubscribed;
      const userCreatorWithoutFamily = family && !family.duplicated && !family.inProcess &&
          (!userActiveFamily || !family.familyNotUnsubscribed) && !invitationPending && userHasCreatorRole;
      const userCreatorWithFamily = family && !family.duplicated && !family.inProcess &&
          userActiveFamily && userHasCreatorRole && family.familyNotUnsubscribed;
      const userNoCreatorWithFamily = !invitationPending && !userHasCreatorRole && userActiveFamily && family.familyNotUnsubscribed;
      const userToInfo = userCreatorWithoutFamily || userCreatorWithFamily || userNoCreatorWithFamily;
      const fromDirectionForward = direction && direction === 'forward';
      const productId = (inconsistentData || minorInMoreFamilies || userNoCreatorWithoutFamily || !userActiveFamily) ? null :
          family.canBeCreator && !!family.isCreator;
      if (!fromReturn) {
        this.or.registerFamiliesOperation(FamiliesOperation.INICIO, '', productId);
      }
      if (inconsistentData) {
        const messageIncossistentData = family && family.canBeCreator ? 'FAMILIES.ERROR.DATA_NOT_CONSISTENT.MESSAGE_MANAGER' : 'FAMILIES.ERROR.DATA_NOT_CONSISTENT.MESSAGE_NO_MANAGER';
        // CU003 - User interface with inconsistent data
        let messageError = 'El usuario no se ha encontrado en RIC';
        if (family && family.duplicated) {
          messageError = 'El usuario se encuentra duplicado en RIC';
        }
        if (family && family.inProcess) {
          messageError = 'El usuario se encuentra en trámites en curso en una familia contractual';
        }
        if (family && !family.canBeInvited) {
          messageError = 'El usuario NO pueda ser invitado a una familia';
        }
        this.or.registerFamiliesOperation(FamiliesOperation.NOT_CONSISTENT_DATA,
            this.familiesService.getStructureFamily(this.auth.getState().user, [],
                FamiliesOperation.NOT_CONSISTENT_DATA, messageError), family.canBeCreator && !!family.isCreator);
        const messageConfig: FamilyInfoConfig = {
          title: this.trans.instant('FAMILIES.ERROR.DATA_NOT_CONSISTENT.TITLE'),
          isMainHeader: true,
          message: this.sanitizer.bypassSecurityTrustHtml(this.trans.instant(messageIncossistentData)),
          imageRef: 'assets/images/families/invitation-rejected.svg',
          buttons: [
            {
              text: this.trans.instant('FAMILIES.ERROR.DATA_NOT_CONSISTENT.BUTTON'),
              fill: 'solid',
              color: 'primary',
              redirect: 'locator/services-view',
            }
          ],
          screenTitleAnalytics: ':modificar familia',
          stepSuccessAnalytics: {screenTitle: ':step error-error gestion', step: 'step-error'}
        };
        this.nav.to('families/response', messageConfig);

      } else if (minorInMoreFamilies) {
        const messageConfig: FamilyInfoConfig = {
          title: this.trans.instant('FAMILIES.ERROR.MORE_THAN_ONE_FAMILY.TITLE'),
          isMainHeader: true,
          imageRef: 'assets/images/families/info.svg',
          screenTitleAnalytics: ':modificar familia',
          stepSuccessAnalytics: {screenTitle: ':step error-error menor familias', step: 'step-error'}
        };
        this.nav.to('families/response', messageConfig);
      } else if (userNoCreatorWithoutFamily) {
        // CU004 - Non-Manager / Creator user interface does not exist in Family
        this.or.registerFamiliesOperation(FamiliesOperation.RELATIVE_WITHOUT_FAMILY, '', false);
        const messageConfig: FamilyInfoConfig = {
          title: this.trans.instant('FAMILIES.ERROR.NO_FAMILY.TITLE'),
          message: this.sanitizer.bypassSecurityTrustHtml(this.trans.instant('FAMILIES.ERROR.NO_FAMILY.MESSAGE')),
          imageRef: 'assets/images/families/info.svg',
          isMainHeader: true,
          buttons: [
            {
              text: this.trans.instant('FAMILIES.ERROR.NO_FAMILY.BUTTON_1'),
              fill: 'solid',
              color: 'primary',
              redirect: 'families/discount-and-benefits',
            },
            {
              text: this.trans.instant('FAMILIES.ERROR.NO_FAMILY.BUTTON_2'),
              color: 'dark',
              redirect: 'locator/services-view',
            }
          ],
          screenTitleAnalytics: ':usuario sin familia'
        };
        this.nav.to('families/response', messageConfig);

      } else if (userToStep1) {
        if (fromDirectionForward) {
          this.nav.to('families/step1', {from: 'families-helper'});
        } else {
          this.nav.root('families/step1', {from: 'families-helper'});
        }
      } else if (userToInfo) {
        if (fromDirectionForward) {
          this.nav.to('families/info', {
            fromReturn,
            fromDraftNotice,
            from: 'families-helper'
          });
        } else {
          this.nav.root('families/info', {
            fromReturn,
            fromDraftNotice,
            from: 'families-helper'
          });
        }
      }
    }, () => {
      this.familiesService.setFamily(null);
      this.or.registerFamiliesOperation(FamiliesOperation.INICIO, '', null);
      this.nav.to('families/response', {error: this.trans.instant('FAMILIES.ERROR.GENERIC.MESSAGE')});
    });
   }
 }
